import { msalInstance } from '../App';
import { loginRequest, tokenRenewalConfig } from '../authConfig';
import config from '../config';

// Time before token expiry to trigger renewal (5 minutes)
const TOKEN_RENEWAL_OFFSET_MS = 5 * 60 * 1000;

class AuthService {
    /**
     * Log authentication events to backend
     */
    async logAuthEvent(eventType, additionalData = {}) {
        try {
            const account = this.getAccount();
            const logData = {
                event_type: eventType,
                user_email: account?.username || 'Unknown',
                timestamp: new Date().toISOString(),
                ...additionalData
            };

            // Send to backend for logging
            await fetch(`${config.apiUrl}/api/auth/log-event`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(logData)
            });

            // Also log to console in development
            if (config.environment !== 'prod') {
                console.log(`[AUTH] ${eventType}`, logData);
            }
        } catch (error) {
            console.error('Failed to log auth event:', error);
        }
    }

    /**
     * Get active account
     */
    getAccount() {
        const accounts = msalInstance.getAllAccounts();
        return accounts.length > 0 ? accounts[0] : null;
    }

    /**
     * Get token silently and handle auto renewal
     */
    async acquireToken() {
        const account = this.getAccount();

        if (!account) {
            await this.logAuthEvent('TOKEN_ACQUISITION_FAILED', {
                error_message: 'No active account'
            });
            throw new Error('No active account! Sign in required.');
        }

        try {
            const response = await msalInstance.acquireTokenSilent({
                ...loginRequest,
                account: account,
            });

            // Log successful token renewal
            await this.logAuthEvent('TOKEN_RENEWAL_SUCCESS', {
                user_email: account.username,
                expires_on: response.expiresOn
            });

            // Set timer for token renewal
            this.scheduleTokenRenewal(response.expiresOn);

            return response.accessToken;
        } catch (error) {
            console.error('Silent token acquisition failed', error);

            // Log token renewal failure with details
            await this.logAuthEvent('TOKEN_RENEWAL_FAILURE', {
                error_code: error.errorCode,
                error_message: error.errorMessage || error.message,
                correlation_id: error.correlationId
            });

            // If silent renewal fails, redirect to login
            if (error.name === 'InteractionRequiredAuthError') {
                msalInstance.acquireTokenRedirect(loginRequest);
            }
            throw error;
        }
    }

    /**
     * Schedule token renewal before it expires
     */
    scheduleTokenRenewal(expiresOn) {
        if (!expiresOn) return;
        
        const expiresOnDate = new Date(expiresOn);
        const renewalTime = expiresOnDate.getTime() - TOKEN_RENEWAL_OFFSET_MS;
        const timeUntilRenewal = renewalTime - Date.now();
        
        if (timeUntilRenewal <= 0) {
            // Token is already expired or about to expire
            this.acquireToken().catch(console.error);
            return;
        }
        
        // Clear any existing renewal timers
        if (this.tokenRenewalTimeout) {
            clearTimeout(this.tokenRenewalTimeout);
        }
        
        // Set timer to renew token before it expires
        this.tokenRenewalTimeout = setTimeout(() => {
            this.acquireToken().catch(console.error);
        }, timeUntilRenewal);
        
        console.log(`Token renewal scheduled in ${Math.round(timeUntilRenewal / 1000)} seconds`);
    }

    /**
     * Logout user
     */
    async logout() {
        const account = this.getAccount();

        // Log logout event
        await this.logAuthEvent('LOGOUT', {
            user_email: account?.username
        });

        // Clear any token renewal timers
        if (this.tokenRenewalTimeout) {
            clearTimeout(this.tokenRenewalTimeout);
        }

        return msalInstance.logoutRedirect();
    }

    /**
     * Check if session is active by verifying token
     */
    async isSessionActive() {
        try {
            await this.acquireToken();
            return true;
        } catch (error) {
            console.error('Session validation failed', error);
            return false;
        }
    }

    /**
     * Set up inactivity monitoring
     * @param {number} inactivityTimeoutMs - Timeout in milliseconds
     */
    setupInactivityMonitor(inactivityTimeoutMs = 30 * 60 * 1000) { // Default 30 min
        let inactivityTimer;

        const resetInactivityTimer = () => {
            if (inactivityTimer) clearTimeout(inactivityTimer);

            inactivityTimer = setTimeout(async () => {
                console.log('User inactive, logging out...');
                await this.logAuthEvent('INACTIVITY_LOGOUT');
                this.logout();
            }, inactivityTimeoutMs);
        };

        // Reset timer on user activity
        ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach(event => {
            document.addEventListener(event, resetInactivityTimer, false);
        });

        // Initial setup
        resetInactivityTimer();
    }
}

export const authService = new AuthService();
export default authService;